import { useState, type ReactNode } from 'react'
import {
  ChevronLeft,
  ChevronRight,
  ChevronsLeft,
  ChevronsRight,
  ChevronUp,
  ChevronDown,
  ChevronsUpDown,
  Search,
} from 'lucide-react'
import Button, { cn } from './Button'

export type SortDir = 'asc' | 'desc'

export interface Column<T> {
  key: string
  header: ReactNode
  cell: (row: T) => ReactNode
  className?: string
  sortable?: boolean
}

interface DataTableProps<T> {
  columns: Column<T>[]
  rows: T[]
  keyExtractor: (row: T) => string
  page?: number
  pageSize?: number
  total?: number
  onPageChange?: (page: number) => void
  searchable?: boolean
  searchValue?: string
  onSearchChange?: (value: string) => void
  searchPlaceholder?: string
  isLoading?: boolean
  emptyMessage?: string
  actions?: ReactNode
  sortKey?: string | null
  sortDir?: SortDir
  onSort?: (key: string) => void
}

export default function DataTable<T>({
  columns,
  rows,
  keyExtractor,
  page = 1,
  pageSize = 25,
  total = rows.length,
  onPageChange,
  searchable,
  searchValue = '',
  onSearchChange,
  searchPlaceholder = 'Search…',
  isLoading,
  emptyMessage = 'No data found.',
  actions,
  sortKey = null,
  sortDir = 'asc',
  onSort,
}: DataTableProps<T>) {
  const isControlled = !!onPageChange

  const [localPage, setLocalPage] = useState(1)
  const [localSearch, setLocalSearch] = useState('')

  const effectiveSearch = isControlled ? searchValue : localSearch
  const effectivePage = isControlled ? page : localPage

  const filteredRows = searchable && !isControlled
    ? rows.filter((row) =>
        columns.some((col) => {
          const value = col.cell(row)
          if (value == null) return false
          return String(value).toLowerCase().includes(localSearch.toLowerCase())
        })
      )
    : rows

  const displayRows = searchable && !isControlled
    ? filteredRows.slice((effectivePage - 1) * pageSize, effectivePage * pageSize)
    : rows

  const displayTotal = searchable && !isControlled ? filteredRows.length : total
  const displayPages = Math.max(1, Math.ceil(displayTotal / pageSize))

  const setPage = (p: number) => {
    const next = Math.max(1, Math.min(displayPages, p))
    if (isControlled) {
      onPageChange(next)
    } else {
      setLocalPage(next)
    }
  }

  const handleSearch = (value: string) => {
    if (isControlled && onSearchChange) {
      onSearchChange(value)
    } else {
      setLocalSearch(value)
      setLocalPage(1)
    }
  }

  return (
    <div className="bg-white rounded-xl border shadow-sm overflow-hidden">
      {(searchable || actions) && (
        <div className="px-4 py-3 border-b flex flex-col sm:flex-row sm:items-center gap-3">
          {searchable && (
            <div className="relative flex-1 max-w-md">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                type="text"
                value={effectiveSearch}
                onChange={(e) => handleSearch(e.target.value)}
                placeholder={searchPlaceholder}
                className="w-full pl-9 pr-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-brand-500"
              />
            </div>
          )}
          {actions && <div className="flex items-center gap-2 ml-auto">{actions}</div>}
        </div>
      )}
      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200 text-sm">
          <thead className="bg-gray-50">
            <tr>
              {columns.map((col) => {
                const canSort = col.sortable && !!onSort
                const isActive = sortKey === col.key
                return (
                  <th
                    key={col.key}
                    className={cn(
                      'px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider',
                      col.className
                    )}
                  >
                    {canSort ? (
                      <button
                        type="button"
                        onClick={() => onSort!(col.key)}
                        className={cn(
                          'group inline-flex items-center gap-1 hover:text-gray-700',
                          isActive && 'text-gray-900'
                        )}
                      >
                        <span>{col.header}</span>
                        {isActive ? (
                          sortDir === 'asc' ? (
                            <ChevronUp className="w-3.5 h-3.5" />
                          ) : (
                            <ChevronDown className="w-3.5 h-3.5" />
                          )
                        ) : (
                          <ChevronsUpDown className="w-3.5 h-3.5 text-gray-300 group-hover:text-gray-400" />
                        )}
                      </button>
                    ) : (
                      col.header
                    )}
                  </th>
                )
              })}
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {isLoading ? (
              <tr>
                <td colSpan={columns.length} className="px-4 py-8 text-center text-gray-500">
                  Loading…
                </td>
              </tr>
            ) : displayRows.length === 0 ? (
              <tr>
                <td colSpan={columns.length} className="px-4 py-8 text-center text-gray-500">
                  {emptyMessage}
                </td>
              </tr>
            ) : (
              displayRows.map((row) => (
                <tr key={keyExtractor(row)} className="hover:bg-gray-50">
                  {columns.map((col) => (
                    <td key={col.key} className={cn('px-4 py-3 text-gray-700', col.className)}>
                      {col.cell(row)}
                    </td>
                  ))}
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
      {displayTotal > 0 && (
        <div className="px-4 py-3 border-t flex flex-col sm:flex-row sm:items-center justify-between gap-3">
          <span className="text-sm text-gray-600">
            Showing {Math.min(displayTotal, (effectivePage - 1) * pageSize + 1)}–
            {Math.min(displayTotal, effectivePage * pageSize)} of {displayTotal}
          </span>
          <div className="flex items-center gap-2">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setPage(1)}
              disabled={effectivePage === 1}
              aria-label="First page"
            >
              <ChevronsLeft className="w-4 h-4" />
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setPage(effectivePage - 1)}
              disabled={effectivePage === 1}
              aria-label="Previous page"
            >
              <ChevronLeft className="w-4 h-4" />
            </Button>
            <span className="text-sm text-gray-600 px-2">
              Page {effectivePage} of {displayPages}
            </span>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setPage(effectivePage + 1)}
              disabled={effectivePage >= displayPages}
              aria-label="Next page"
            >
              <ChevronRight className="w-4 h-4" />
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setPage(displayPages)}
              disabled={effectivePage >= displayPages}
              aria-label="Last page"
            >
              <ChevronsRight className="w-4 h-4" />
            </Button>
          </div>
        </div>
      )}
    </div>
  )
}
